import React, { useState, useEffect } from 'react';
import { Settings, Package, FileText, Save, ChevronDown, ChevronUp, Image, DollarSign, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState({});
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchSections();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/products`);
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchSections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/sections`);
      const data = await res.json();
      setSections(data);
    } catch (err) {
      console.error('Failed to fetch sections:', err);
    }
  };

  const saveProduct = async (productId, updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        showToast('Product updated successfully');
        fetchProducts();
        setEditingProduct(null);
      } else {
        showToast('Failed to update product', 'error');
      }
    } catch (err) {
      showToast('Error saving product', 'error');
    }
    setSaving(false);
  };

  const saveSectionContent = async (section, key, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section, key, value }),
      });
      if (res.ok) {
        showToast(`Updated ${section} → ${key}`);
        setSections(prev => ({
          ...prev,
          [section]: { ...prev[section], [key]: value }
        }));
      } else {
        showToast('Failed to save', 'error');
      }
    } catch (err) {
      showToast('Error saving content', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-page">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 border ${
          toast.type === 'error' ? 'bg-red-900/90 border-red-500' : 'bg-[#0a0a0a] border-[#D4AF37]'
        }`}>
          <p className="font-body text-sm text-white">{toast.message}</p>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-[#D4AF37]/20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings size={20} className="text-[#D4AF37]" />
            <h1 className="font-heading text-lg tracking-[0.15em] text-white">
              ADMIN <span className="text-[#D4AF37]">DASHBOARD</span>
            </h1>
          </div>
          <a href="/" className="font-heading text-xs tracking-wider text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors">
            VIEW SITE
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[#D4AF37]/10 pb-4">
          {[
            { id: 'products', label: 'PRODUCTS', icon: Package },
            { id: 'sections', label: 'SITE CONTENT', icon: FileText },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 font-heading text-xs tracking-wider border transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#D4AF37] text-[#050505] border-[#D4AF37]'
                  : 'text-white/60 border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-sm tracking-[0.2em] text-[#D4AF37]">ALL PRODUCTS ({products.length})</h2>
            </div>

            <div className="space-y-3">
              {products.map(product => (
                <ProductEditor
                  key={product.id}
                  product={product}
                  isEditing={editingProduct === product.id}
                  onEdit={() => setEditingProduct(editingProduct === product.id ? null : product.id)}
                  onSave={(updates) => saveProduct(product.id, updates)}
                  saving={saving}
                />
              ))}
            </div>
          </div>
        )}

        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div>
            <h2 className="font-heading text-sm tracking-[0.2em] text-[#D4AF37] mb-6">EDITABLE SITE SECTIONS</h2>
            <div className="space-y-3">
              {Object.entries(sections).map(([sectionName, fields]) => (
                <SectionEditor
                  key={sectionName}
                  sectionName={sectionName}
                  fields={fields}
                  expanded={expandedSection === sectionName}
                  onToggle={() => setExpandedSection(expandedSection === sectionName ? null : sectionName)}
                  onSave={saveSectionContent}
                  saving={saving}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProductEditor = ({ product, isEditing, onEdit, onSave, saving }) => {
  const [form, setForm] = useState({
    name: product.name,
    subtitle: product.subtitle,
    price: product.price,
    description: product.description,
    image: product.image,
    category: product.category,
    size: product.size,
    in_stock: product.in_stock,
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className={`border ${isEditing ? 'border-[#D4AF37]' : 'border-[#D4AF37]/15'} bg-[#0a0a0a] transition-colors`}>
      {/* Product Row */}
      <div
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-[#D4AF37]/5 transition-colors"
        onClick={onEdit}
        data-testid={`product-editor-${product.id}`}
      >
        <img src={product.image} alt={product.name} className="w-14 h-18 object-cover border border-[#D4AF37]/20" />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm text-white">{product.name}</h3>
          <p className="font-body text-xs text-white/40">{product.subtitle}</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-heading text-sm text-[#D4AF37]">${product.price?.toFixed(2)}</span>
          <span className={`font-body text-[10px] px-2 py-0.5 ${product.in_stock ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
            {product.in_stock ? 'IN STOCK' : 'OUT OF STOCK'}
          </span>
          {isEditing ? <ChevronUp size={16} className="text-[#D4AF37]" /> : <ChevronDown size={16} className="text-white/30" />}
        </div>
      </div>

      {/* Edit Form */}
      {isEditing && (
        <div className="px-4 pb-4 border-t border-[#D4AF37]/10 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">NAME</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full bg-[#050505] border border-[#D4AF37]/20 px-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none"
                data-testid={`edit-name-${product.id}`}
              />
            </div>
            <div>
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">SUBTITLE</label>
              <input
                type="text"
                value={form.subtitle}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                className="w-full bg-[#050505] border border-[#D4AF37]/20 px-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div>
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">PRICE ($)</label>
              <div className="relative">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]/40" />
                <input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => handleChange('price', parseFloat(e.target.value))}
                  className="w-full bg-[#050505] border border-[#D4AF37]/20 pl-8 pr-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none"
                  data-testid={`edit-price-${product.id}`}
                />
              </div>
            </div>
            <div>
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">CATEGORY</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full bg-[#050505] border border-[#D4AF37]/20 px-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">IMAGE URL</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Image size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]/40" />
                  <input
                    type="text"
                    value={form.image}
                    onChange={(e) => handleChange('image', e.target.value)}
                    className="w-full bg-[#050505] border border-[#D4AF37]/20 pl-8 pr-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none"
                    data-testid={`edit-image-${product.id}`}
                  />
                </div>
                {form.image && (
                  <img src={form.image} alt="Preview" className="w-10 h-10 object-cover border border-[#D4AF37]/20" />
                )}
              </div>
            </div>
            <div className="col-span-2">
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">DESCRIPTION</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full bg-[#050505] border border-[#D4AF37]/20 px-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none resize-none"
              />
            </div>
            <div>
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">SIZE</label>
              <input
                type="text"
                value={form.size}
                onChange={(e) => handleChange('size', e.target.value)}
                className="w-full bg-[#050505] border border-[#D4AF37]/20 px-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.in_stock}
                  onChange={(e) => handleChange('in_stock', e.target.checked)}
                  className="accent-[#D4AF37]"
                />
                <span className="font-heading text-xs text-white/60">IN STOCK</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-4 pt-4 border-t border-[#D4AF37]/10">
            <button
              onClick={() => onSave(form)}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-[#050505] font-heading text-xs tracking-wider hover:bg-[#E5C158] transition-colors disabled:opacity-50"
              data-testid={`save-product-${product.id}`}
            >
              <Save size={14} />
              {saving ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
            <button
              onClick={onEdit}
              className="px-4 py-2 border border-[#D4AF37]/20 text-white/50 font-heading text-xs tracking-wider hover:border-[#D4AF37]/40 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionEditor = ({ sectionName, fields, expanded, onToggle, onSave, saving }) => {
  const [localFields, setLocalFields] = useState(fields);

  useEffect(() => {
    setLocalFields(fields);
  }, [fields]);

  const handleFieldChange = (key, value) => {
    setLocalFields(prev => ({ ...prev, [key]: value }));
  };

  const sectionLabels = {
    hero: 'Hero Section',
    treasury: 'The Treasury (Arch Doors)',
    heritage: 'Heritage & Map Section',
    membership: 'Membership Section',
    footer: 'Footer',
  };

  return (
    <div className={`border ${expanded ? 'border-[#D4AF37]' : 'border-[#D4AF37]/15'} bg-[#0a0a0a]`}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#D4AF37]/5 transition-colors"
        onClick={onToggle}
        data-testid={`section-editor-${sectionName}`}
      >
        <div>
          <h3 className="font-heading text-sm text-white">{sectionLabels[sectionName] || sectionName.toUpperCase()}</h3>
          <p className="font-body text-[10px] text-white/30">{Object.keys(fields).length} editable fields</p>
        </div>
        {expanded ? <ChevronUp size={16} className="text-[#D4AF37]" /> : <ChevronDown size={16} className="text-white/30" />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-[#D4AF37]/10 pt-4 space-y-4">
          {Object.entries(localFields).map(([key, value]) => (
            <div key={key}>
              <label className="font-heading text-[10px] tracking-wider text-[#D4AF37]/60 block mb-1">
                {key.replace(/_/g, ' ').toUpperCase()}
              </label>
              {value.length > 80 ? (
                <textarea
                  value={localFields[key]}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  rows={3}
                  className="w-full bg-[#050505] border border-[#D4AF37]/20 px-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none resize-none"
                />
              ) : (
                <input
                  type="text"
                  value={localFields[key]}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  className="w-full bg-[#050505] border border-[#D4AF37]/20 px-3 py-2 text-white font-body text-sm focus:border-[#D4AF37] outline-none"
                  data-testid={`edit-${sectionName}-${key}`}
                />
              )}
              <button
                onClick={() => onSave(sectionName, key, localFields[key])}
                disabled={saving || localFields[key] === fields[key]}
                className={`mt-1 px-3 py-1 font-heading text-[10px] tracking-wider transition-colors ${
                  localFields[key] !== fields[key]
                    ? 'bg-[#D4AF37] text-[#050505] hover:bg-[#E5C158]'
                    : 'bg-[#D4AF37]/10 text-[#D4AF37]/30 cursor-not-allowed'
                }`}
              >
                SAVE
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
